import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Cpu, Globe, Zap, Microchip, Binary } from "lucide-react";
import { IMAGES } from "@/assets/images";
import { ApplicationCard, InitiativeCard } from "@/components/Cards";
import { ParticleAnimation, QuantumWave } from "@/components/QuantumVisualizations";
import { quantumApplications, bahrainInitiatives } from "@/data/index";

export default function Future() {
  // Filter for future-oriented applications or highlights
  const futureTech = [
    {
      title: "Quantum Internet",
      icon: <Globe className="w-6 h-6" />,
      description: "A global network of quantum repeaters enabling perfectly secure communication through entanglement distribution.",
      readiness: "2028-2032 Expected"
    },
    {
      title: "Fault-Tolerant Computing",
      icon: <Cpu className="w-6 h-6" />,
      description: "Transitioning from Noisy Intermediate-Scale Quantum (NISQ) to error-corrected systems capable of breaking RSA.",
      readiness: "2030 Strategic Milestone"
    },
    {
      title: "Molecular Design",
      icon: <Microchip className="w-6 h-6" />,
      description: "Atomic-level engineering of new materials for superconductors and carbon capture systems.",
      readiness: "Active Deployment"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-30">
          <img 
            src={IMAGES.QUANTUM_COMPUTING_5} 
            alt="Quantum Future" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/20 via-transparent to-background" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 border border-primary/20">
              The Second Quantum Revolution
            </span>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
              2026 and <span className="text-accent">Beyond</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              The first century of quantum physics was about understanding nature. 
              The second is about engineering it. We are entering the era of total coherence.
            </p>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 w-full opacity-20">
          <QuantumWave className="h-32 w-full" />
        </div>
      </section>

      {/* Revolution Vision */}
      <section className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <h2 className="text-4xl font-bold">From Observation to Control</h2>
              <div className="space-y-6 text-lg text-muted-foreground">
                <p>
                  In 1925, Heisenberg and Schrödinger gave us the tools to observe the microscopic world. 
                  For 100 years, we built lasers and transistors based on these observations.
                </p>
                <p>
                  Today, in 2026, we have moved beyond simple observation. We now actively manipulate individual 
                  qubits, entangling atoms across distances and orchestrating wave function collapses to solve 
                  the world's most complex equations.
                </p>
              </div>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border shadow-sm">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    <Binary className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold">Active Qubit Control</p>
                    <p className="text-xs text-muted-foreground">Real-time error correction</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border shadow-sm">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center text-accent">
                    <Zap className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold">Zero-Loss Power</p>
                    <p className="text-xs text-muted-foreground">Quantum superconductors</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative aspect-square rounded-3xl overflow-hidden border border-border shadow-2xl"
            >
              <img 
                src={IMAGES.PARTICLE_PHYSICS_5} 
                alt="Quantum Simulation" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-primary/10 mix-blend-overlay" />
              <div className="absolute inset-0 flex items-center justify-center">
                <ParticleAnimation className="w-full h-full" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Emerging Frontiers Grid */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Emerging Frontiers</h2>
            <p className="text-muted-foreground">Technologies reaching maturity in the next decade</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {futureTech.map((tech, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="p-8 rounded-2xl bg-card border border-border hover:border-primary/30 transition-all hover:shadow-lg group"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  {tech.icon}
                </div>
                <h3 className="text-xl font-bold mb-3">{tech.title}</h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  {tech.description}
                </p>
                <div className="pt-6 border-t border-border">
                  <span className="text-xs font-mono uppercase tracking-wider text-accent font-semibold">
                    {tech.readiness}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bahrain's Strategic Role */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-16 items-center">
            <div className="w-full md:w-1/2 order-2 md:order-1">
              <div className="grid grid-cols-1 gap-6">
                {bahrainInitiatives.slice(2, 4).map((initiative) => (
                  <InitiativeCard key={initiative.id} initiative={initiative} />
                ))}
              </div>
            </div>
            <div className="w-full md:w-1/2 order-1 md:order-2">
              <h2 className="text-4xl font-bold mb-6">Bahrain as a Quantum Hub</h2>
              <p className="text-lg text-muted-foreground mb-8">
                By 2030, the Kingdom of Bahrain aims to be the leading regional center for quantum-classical hybrid systems. 
                Through strategic sovereign investments and academic excellence, we are building a workforce ready for the 
                next phase of the digital economy.
              </p>
              <ul className="space-y-4">
                {[
                  "Regional Quantum Key Distribution (QKD) Network",
                  "Sovereign AI-Quantum Drug Discovery Pipelines",
                  "National Talent Development in Quantum Computing"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-accent" />
                    <span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.RESEARCH_COLLABORATION_4} 
            alt="Collaboration" 
            className="w-full h-full object-cover opacity-10"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto p-12 rounded-3xl bg-primary text-primary-foreground text-center shadow-2xl">
            <h2 className="text-4xl font-bold mb-6">Be Part of the Centennial</h2>
            <p className="text-xl opacity-90 mb-10">
              The quantum future isn't just coming—it's being built right now in Bahrain. 
              Join our research ecosystem and define the next 100 years.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button className="px-8 py-4 bg-white text-primary rounded-full font-bold hover:bg-opacity-90 transition-colors flex items-center gap-2 group">
                Explore Research Opportunities <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button className="px-8 py-4 bg-primary-foreground/10 border border-white/20 rounded-full font-bold hover:bg-white/10 transition-colors">
                Download 2026 Vision Paper
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Final Visualization */}
      <div className="h-48 flex items-end">
        <QuantumWave className="w-full h-24 text-accent/20" />
      </div>
    </div>
  );
}
